/**
 * Leviathan - Lead Detail Page
 * Quasar Prime LLC
 *
 * View and manage a single lead with activities and competitors
 */

import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  Globe,
  MapPin,
  Calendar,
  Target,
  TrendingUp,
  Users,
  Clock,
  CheckCircle,
  Plus,
  Building2,
  UserCheck,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';
import { API_URL } from '@/config/api';
import { fullName } from '@/utils/personName';

// Lead status configuration
const LEAD_STATUSES = [
  { value: 'NEW', label: 'New', color: 'bg-blue-500' },
  { value: 'CONTACTED', label: 'Contacted', color: 'bg-cyan-500' },
  { value: 'QUALIFIED', label: 'Qualified', color: 'bg-indigo-500' },
  { value: 'PROPOSAL', label: 'Proposal', color: 'bg-purple-500' },
  { value: 'NEGOTIATION', label: 'Negotiation', color: 'bg-amber-500' },
  { value: 'WON', label: 'Won', color: 'bg-emerald-500' },
  { value: 'LOST', label: 'Lost', color: 'bg-red-500' },
  { value: 'DORMANT', label: 'Dormant', color: 'bg-slate-400' },
];

const SOURCE_LABELS: Record<string, string> = {
  WEBSITE: 'Website',
  REFERRAL: 'Referral',
  COLD_CALL: 'Cold Call',
  TRADE_SHOW: 'Trade Show',
  ADVERTISEMENT: 'Advertisement',
  SOCIAL_MEDIA: 'Social Media',
  EXISTING_CUSTOMER: 'Existing Customer',
  PARTNER: 'Partner',
  OTHER: 'Other',
};

const ACTIVITY_ICONS: Record<string, typeof Phone> = {
  CALL: Phone,
  EMAIL: Mail,
  MEETING: Users,
  TASK: CheckCircle,
  NOTE: Target,
  SITE_VISIT: MapPin,
  QUOTE_SENT: TrendingUp,
  FOLLOW_UP: Clock,
};

interface Lead {
  id: string;
  leadNumber: string;
  companyName: string;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactTitle: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  source: string;
  status: string;
  industryType: string | null;
  estimatedVolume: number | null;
  estimatedValue: number | null;
  products: string[];
  currentSupplier: string | null;
  contractEndDate: string | null;
  score: number;
  scoreFactors: {
    volumeScore: number;
    contactScore: number;
    engagementScore: number;
    competitionScore: number;
    total: number;
  } | null;
  lastContactDate: string | null;
  nextFollowUpDate: string | null;
  followUpNotes: string | null;
  notes: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  assignedTo?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  };
  customer?: {
    id: string;
    name: string;
    customerNumber: string;
  };
  activities: Activity[];
  competitors: Competitor[];
}

interface Activity {
  id: string;
  type: string;
  subject: string;
  description: string | null;
  status: string;
  scheduledDate: string | null;
  completedDate: string | null;
  outcome: string | null;
  createdAt: string;
  createdBy?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  };
}

interface Competitor {
  id: string;
  name: string;
  website: string | null;
  strengths: string | null;
  weaknesses: string | null;
  pricing: string | null;
  notes: string | null;
}

interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  const [showStatusChange, setShowStatusChange] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showAddCompetitor, setShowAddCompetitor] = useState(false);
  const [showConvert, setShowConvert] = useState(false);

  // Fetch lead details
  const { data: lead, isLoading } = useQuery<Lead>({
    queryKey: ['lead', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/leads/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch lead');
      return res.json();
    },
    enabled: !!id,
  });

  // Fetch users for assignment
  const { data: users } = useQuery<User[]>({
    queryKey: ['users-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      return data.data || data;
    },
  });

  // Update status mutation
  const statusMutation = useMutation({
    mutationFn: async ({ status, notes }: { status: string; notes?: string }) => {
      const res = await fetch(`${API_URL}/api/leads/${id}/status`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, notes }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      setShowStatusChange(false);
    },
  });

  // Assign mutation
  const assignMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await fetch(`${API_URL}/api/leads/${id}/assign`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });
      if (!res.ok) throw new Error('Failed to assign lead');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      setShowAssign(false);
    },
  });

  // Recalculate score mutation
  const scoreMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/leads/${id}/score`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to recalculate score');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/leads/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to delete lead');
      return res.json();
    },
    onSuccess: () => {
      navigate('/sales/leads');
    },
  });

  // Convert to customer mutation
  const convertMutation = useMutation({
    mutationFn: async (data: { creditLimit?: number; creditTerms?: number }) => {
      const res = await fetch(`${API_URL}/api/leads/${id}/convert`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to convert lead');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      setShowConvert(false);
      // Navigate to new customer
      if (data.customer?.id) {
        navigate(`/customers/${data.customer.id}`);
      }
    },
  });

  // Add activity mutation
  const addActivityMutation = useMutation({
    mutationFn: async (data: {
      type: string;
      subject: string;
      description?: string;
      scheduledDate?: string;
    }) => {
      const res = await fetch(`${API_URL}/api/activities`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...data, leadId: id }),
      });
      if (!res.ok) throw new Error('Failed to add activity');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      setShowAddActivity(false);
    },
  });

  // Add competitor mutation
  const addCompetitorMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      website?: string;
      strengths?: string;
      weaknesses?: string;
      pricing?: string;
    }) => {
      const res = await fetch(`${API_URL}/api/leads/${id}/competitors`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to add competitor');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      setShowAddCompetitor(false);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading lead...</div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Lead not found</div>
      </div>
    );
  }

  const statusConfig = LEAD_STATUSES.find((s) => s.value === lead.status);
  const isConverted = lead.status === 'WON' && lead.customer;
  const isClosed = lead.status === 'WON' || lead.status === 'LOST';

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-emerald-400';
    if (score >= 40) return 'text-amber-400';
    return 'text-slate-400';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/sales/leads"
            className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-100">{lead.companyName}</h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium text-white ${statusConfig?.color}`}
              >
                {statusConfig?.label}
              </span>
            </div>
            <p className="text-slate-400">{lead.leadNumber}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isClosed && (
            <button
              onClick={() => setShowConvert(true)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors"
            >
              <UserCheck className="w-4 h-4" />
              Convert to Customer
            </button>
          )}
          <Link
            to={`/sales/leads/${id}/edit`}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg font-medium transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </Link>
          <button
            onClick={() => {
              if (confirm('Are you sure you want to delete this lead?')) {
                deleteMutation.mutate();
              }
            }}
            className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Converted Customer Link */}
      {isConverted && lead.customer && (
        <div className="bg-emerald-500/10 border border-emerald-500/50 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            <p className="text-emerald-400">
              This lead has been converted to customer{' '}
              <span className="font-semibold">{lead.customer.customerNumber}</span>
            </p>
          </div>
          <Link
            to={`/customers/${lead.customer.id}`}
            className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
          >
            View Customer <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Lead Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Actions */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowStatusChange(true)}
                disabled={isClosed}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                Change Status
              </button>
              <button
                onClick={() => setShowAssign(true)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg text-sm font-medium transition-colors"
              >
                <Users className="w-4 h-4" />
                Assign
              </button>
              <button
                onClick={() => setShowAddActivity(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg text-sm font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Log Activity
              </button>
              <button
                onClick={() => scoreMutation.mutate()}
                disabled={scoreMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg text-sm font-medium transition-colors"
              >
                <RefreshCw
                  className={`w-4 h-4 ${scoreMutation.isPending ? 'animate-spin' : ''}`}
                />
                Recalculate Score
              </button>
            </div>
          </div>

          {/* Contact & Company Info */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <h2 className="text-lg font-semibold text-slate-100 mb-4">
              Contact Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {fullName({ firstName: lead.contactFirstName, lastName: lead.contactLastName }) && (
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-slate-200">{fullName({ firstName: lead.contactFirstName, lastName: lead.contactLastName })}</p>
                      {lead.contactTitle && (
                        <p className="text-sm text-slate-500">{lead.contactTitle}</p>
                      )}
                    </div>
                  </div>
                )}
                {lead.email && (
                  <a
                    href={`mailto:${lead.email}`}
                    className="flex items-center gap-3 text-slate-300 hover:text-amber-400"
                  >
                    <Mail className="w-5 h-5 text-slate-500" />
                    {lead.email}
                  </a>
                )}
                {lead.phone && (
                  <a
                    href={`tel:${lead.phone}`}
                    className="flex items-center gap-3 text-slate-300 hover:text-amber-400"
                  >
                    <Phone className="w-5 h-5 text-slate-500" />
                    {lead.phone}
                  </a>
                )}
                {lead.website && (
                  <a
                    href={lead.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-slate-300 hover:text-amber-400"
                  >
                    <Globe className="w-5 h-5 text-slate-500" />
                    {lead.website}
                  </a>
                )}
              </div>
              <div className="space-y-4">
                {(lead.address || lead.city) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-slate-500 mt-0.5" />
                    <div>
                      {lead.address && <p className="text-slate-200">{lead.address}</p>}
                      {(lead.city || lead.state || lead.zip) && (
                        <p className="text-slate-400">
                          {[lead.city, lead.state, lead.zip].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {lead.industryType && (
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-slate-500" />
                    <span className="text-slate-300">{lead.industryType}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Opportunity Details */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <h2 className="text-lg font-semibold text-slate-100 mb-4">
              Opportunity Details
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-900/50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase tracking-wider">
                  Est. Value
                </p>
                <p className="text-xl font-bold text-emerald-400 mt-1">
                  {lead.estimatedValue
                    ? `$${lead.estimatedValue.toLocaleString()}`
                    : '-'}
                </p>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase tracking-wider">
                  Est. Volume
                </p>
                <p className="text-xl font-bold text-slate-200 mt-1">
                  {lead.estimatedVolume
                    ? `${lead.estimatedVolume.toLocaleString()} gal`
                    : '-'}
                </p>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Source</p>
                <p className="text-xl font-bold text-slate-200 mt-1">
                  {SOURCE_LABELS[lead.source] || lead.source}
                </p>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase tracking-wider">
                  Current Supplier
                </p>
                <p className="text-xl font-bold text-slate-200 mt-1">
                  {lead.currentSupplier || '-'}
                </p>
              </div>
            </div>

            {lead.products && lead.products.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-slate-400 mb-2">Interested Products</p>
                <div className="flex flex-wrap gap-2">
                  {lead.products.map((product, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-amber-500/10 text-amber-500 rounded-full text-sm"
                    >
                      {product}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {lead.contractEndDate && (
              <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 text-sm">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Contract ends: {new Date(lead.contractEndDate).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {/* Activities Timeline */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-100">Activities</h2>
              <button
                onClick={() => setShowAddActivity(true)}
                className="text-amber-500 hover:text-amber-400 text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Activity
              </button>
            </div>

            {lead.activities && lead.activities.length > 0 ? (
              <div className="space-y-4">
                {lead.activities.map((activity) => {
                  const IconComponent = ACTIVITY_ICONS[activity.type] || Clock;
                  return (
                    <div
                      key={activity.id}
                      className="flex gap-4 p-4 bg-slate-900/50 rounded-lg"
                    >
                      <div className="p-2 bg-amber-500/10 rounded-lg h-fit">
                        <IconComponent className="w-4 h-4 text-amber-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-200">
                              {activity.subject}
                            </p>
                            <p className="text-xs text-slate-500">
                              {activity.type.replace('_', ' ')} •{' '}
                              {new Date(activity.createdAt).toLocaleDateString()}
                              {activity.createdBy &&
                                ` • by ${activity.createdBy.firstName} ${activity.createdBy.lastName}`}
                            </p>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${
                              activity.status === 'COMPLETED'
                                ? 'bg-emerald-500/10 text-emerald-400'
                                : activity.status === 'CANCELLED'
                                  ? 'bg-red-500/10 text-red-400'
                                  : 'bg-amber-500/10 text-amber-400'
                            }`}
                          >
                            {activity.status}
                          </span>
                        </div>
                        {activity.description && (
                          <p className="text-sm text-slate-400 mt-2">
                            {activity.description}
                          </p>
                        )}
                        {activity.outcome && (
                          <p className="text-sm text-slate-300 mt-2 p-2 bg-slate-800 rounded">
                            <span className="text-slate-500">Outcome:</span>{' '}
                            {activity.outcome}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No activities logged yet</p>
              </div>
            )}
          </div>

          {/* Competitors */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-100">Competitors</h2>
              <button
                onClick={() => setShowAddCompetitor(true)}
                className="text-amber-500 hover:text-amber-400 text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Competitor
              </button>
            </div>

            {lead.competitors && lead.competitors.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {lead.competitors.map((competitor) => (
                  <div
                    key={competitor.id}
                    className="p-4 bg-slate-900/50 rounded-lg border border-slate-700/50"
                  >
                    <p className="font-medium text-slate-200">{competitor.name}</p>
                    {competitor.website && (
                      <a
                        href={competitor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-amber-500 hover:text-amber-400"
                      >
                        {competitor.website}
                      </a>
                    )}
                    {competitor.strengths && (
                      <p className="text-xs text-slate-400 mt-2">
                        <span className="text-emerald-400">Strengths:</span>{' '}
                        {competitor.strengths}
                      </p>
                    )}
                    {competitor.weaknesses && (
                      <p className="text-xs text-slate-400 mt-1">
                        <span className="text-red-400">Weaknesses:</span>{' '}
                        {competitor.weaknesses}
                      </p>
                    )}
                    {competitor.pricing && (
                      <p className="text-xs text-slate-400 mt-1">
                        <span className="text-amber-400">Pricing:</span>{' '}
                        {competitor.pricing}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No competitors added yet</p>
              </div>
            )}
          </div>

          {/* Notes */}
          {lead.notes && (
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h2 className="text-lg font-semibold text-slate-100 mb-4">Notes</h2>
              <p className="text-slate-300 whitespace-pre-wrap">{lead.notes}</p>
            </div>
          )}
        </div>

        {/* Right Column - Score & Meta */}
        <div className="space-y-6">
          {/* Score Card */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <h2 className="text-lg font-semibold text-slate-100 mb-4">Lead Score</h2>
            <div className="text-center mb-4">
              <div
                className={`text-5xl font-bold ${getScoreColor(lead.score)}`}
              >
                {lead.score}
              </div>
              <p className="text-slate-500 mt-1">out of 100</p>
            </div>

            {lead.scoreFactors && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Volume</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500"
                        style={{
                          width: `${(lead.scoreFactors.volumeScore / 30) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm text-slate-300">
                      {lead.scoreFactors.volumeScore}/30
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Contact</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500"
                        style={{
                          width: `${(lead.scoreFactors.contactScore / 25) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm text-slate-300">
                      {lead.scoreFactors.contactScore}/25
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Engagement</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500"
                        style={{
                          width: `${(lead.scoreFactors.engagementScore / 25) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm text-slate-300">
                      {lead.scoreFactors.engagementScore}/25
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Competition</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500"
                        style={{
                          width: `${(lead.scoreFactors.competitionScore / 20) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm text-slate-300">
                      {lead.scoreFactors.competitionScore}/20
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Assignment */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <h2 className="text-lg font-semibold text-slate-100 mb-4">Assigned To</h2>
            {lead.assignedTo ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/10 rounded-full flex items-center justify-center">
                  <span className="text-amber-500 font-semibold">
                    {lead.assignedTo.firstName?.[0]}
                    {lead.assignedTo.lastName?.[0]}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-slate-200">
                    {lead.assignedTo.firstName} {lead.assignedTo.lastName}
                  </p>
                  <p className="text-sm text-slate-500">{lead.assignedTo.email}</p>
                </div>
              </div>
            ) : (
              <p className="text-slate-500">Unassigned</p>
            )}
            <button
              onClick={() => setShowAssign(true)}
              className="mt-3 text-amber-500 hover:text-amber-400 text-sm"
            >
              {lead.assignedTo ? 'Reassign' : 'Assign'}
            </button>
          </div>

          {/* Follow-up */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <h2 className="text-lg font-semibold text-slate-100 mb-4">Follow-up</h2>
            {lead.nextFollowUpDate ? (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-amber-400">
                  <Calendar className="w-4 h-4" />
                  <span className="font-medium">
                    {new Date(lead.nextFollowUpDate).toLocaleDateString()}
                  </span>
                </div>
                {lead.followUpNotes && (
                  <p className="text-sm text-slate-400 mt-2">{lead.followUpNotes}</p>
                )}
              </div>
            ) : (
              <p className="text-slate-500">No follow-up scheduled</p>
            )}
            {lead.lastContactDate && (
              <p className="text-sm text-slate-500 mt-3">
                Last contact: {new Date(lead.lastContactDate).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Tags */}
          {lead.tags && lead.tags.length > 0 && (
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h2 className="text-lg font-semibold text-slate-100 mb-4">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {lead.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Meta Info */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <h2 className="text-lg font-semibold text-slate-100 mb-4">Details</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Created</span>
                <span className="text-slate-300">
                  {new Date(lead.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Updated</span>
                <span className="text-slate-300">
                  {new Date(lead.updatedAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Activities</span>
                <span className="text-slate-300">{lead.activities?.length || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Change Modal */}
      {showStatusChange && (
        <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700">
            <h3 className="text-lg font-semibold text-slate-100 mb-4">
              Change Lead Status
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {LEAD_STATUSES.map((status) => (
                <button
                  key={status.value}
                  onClick={() => statusMutation.mutate({ status: status.value })}
                  disabled={statusMutation.isPending}
                  className={`p-3 rounded-lg text-left transition-colors ${
                    lead.status === status.value
                      ? `${status.color} text-white`
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowStatusChange(false)}
              className="mt-4 w-full py-2 text-slate-400 hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssign && (
        <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700">
            <h3 className="text-lg font-semibold text-slate-100 mb-4">Assign Lead</h3>
            <select
              onChange={(e) => {
                if (e.target.value) {
                  assignMutation.mutate(e.target.value);
                }
              }}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            >
              <option value="">Select user...</option>
              {users?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowAssign(false)}
              className="mt-4 w-full py-2 text-slate-400 hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Add Activity Modal */}
      {showAddActivity && (
        <AddActivityModal
          onClose={() => setShowAddActivity(false)}
          onSubmit={(data) => addActivityMutation.mutate(data)}
          isLoading={addActivityMutation.isPending}
        />
      )}

      {/* Add Competitor Modal */}
      {showAddCompetitor && (
        <AddCompetitorModal
          onClose={() => setShowAddCompetitor(false)}
          onSubmit={(data) => addCompetitorMutation.mutate(data)}
          isLoading={addCompetitorMutation.isPending}
        />
      )}

      {/* Convert Modal */}
      {showConvert && (
        <ConvertModal
          lead={lead}
          onClose={() => setShowConvert(false)}
          onSubmit={(data) => convertMutation.mutate(data)}
          isLoading={convertMutation.isPending}
        />
      )}
    </div>
  );
}

// Add Activity Modal Component
function AddActivityModal({
  onClose,
  onSubmit,
  isLoading,
}: {
  onClose: () => void;
  onSubmit: (data: {
    type: string;
    subject: string;
    description?: string;
    scheduledDate?: string;
  }) => void;
  isLoading: boolean;
}) {
  const [type, setType] = useState('NOTE');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      type,
      subject,
      description: description || undefined,
      scheduledDate: scheduledDate || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">Log Activity</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100"
            >
              <option value="CALL">Call</option>
              <option value="EMAIL">Email</option>
              <option value="MEETING">Meeting</option>
              <option value="TASK">Task</option>
              <option value="NOTE">Note</option>
              <option value="SITE_VISIT">Site Visit</option>
              <option value="QUOTE_SENT">Quote Sent</option>
              <option value="FOLLOW_UP">Follow-up</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Scheduled Date (optional)
            </label>
            <input
              type="datetime-local"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-slate-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !subject}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save Activity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Add Competitor Modal Component
function AddCompetitorModal({
  onClose,
  onSubmit,
  isLoading,
}: {
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    website?: string;
    strengths?: string;
    weaknesses?: string;
    pricing?: string;
  }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [strengths, setStrengths] = useState('');
  const [weaknesses, setWeaknesses] = useState('');
  const [pricing, setPricing] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      website: website || undefined,
      strengths: strengths || undefined,
      weaknesses: weaknesses || undefined,
      pricing: pricing || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">Add Competitor</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Website
            </label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Strengths
            </label>
            <input
              type="text"
              value={strengths}
              onChange={(e) => setStrengths(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Weaknesses
            </label>
            <input
              type="text"
              value={weaknesses}
              onChange={(e) => setWeaknesses(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Pricing Info
            </label>
            <input
              type="text"
              value={pricing}
              onChange={(e) => setPricing(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-slate-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Add Competitor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Convert Modal Component
function ConvertModal({
  lead,
  onClose,
  onSubmit,
  isLoading,
}: {
  lead: Lead;
  onClose: () => void;
  onSubmit: (data: { creditLimit?: number; creditTerms?: number }) => void;
  isLoading: boolean;
}) {
  const [creditLimit, setCreditLimit] = useState('');
  const [creditTerms, setCreditTerms] = useState('30');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      creditLimit: creditLimit ? parseFloat(creditLimit) : undefined,
      creditTerms: creditTerms ? parseInt(creditTerms, 10) : undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">
          Convert to Customer
        </h3>
        <div className="mb-4 p-3 bg-slate-900/50 rounded-lg">
          <p className="text-slate-300 font-medium">{lead.companyName}</p>
          <p className="text-sm text-slate-500">{lead.leadNumber}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Credit Limit
            </label>
            <input
              type="number"
              value={creditLimit}
              onChange={(e) => setCreditLimit(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Credit Terms (days)
            </label>
            <select
              value={creditTerms}
              onChange={(e) => setCreditTerms(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100"
            >
              <option value="0">COD</option>
              <option value="15">Net 15</option>
              <option value="30">Net 30</option>
              <option value="45">Net 45</option>
              <option value="60">Net 60</option>
            </select>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-slate-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium disabled:opacity-50"
            >
              {isLoading ? 'Converting...' : 'Convert Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
